import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import EmployeeWeeklyViewPage from './weeklyView/EmployeeWeeklyViewPage';
import EmployeeMonthViewPage from './monthView/EmployeeMonthViewPage';
import EmployeeScheduleViewPage from './scheduleView/EmployeeScheduleViewPage';

function getUserDisplayName(user, fallback) {
  if (!user) return fallback;
  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim();
  }
  return user.email || fallback;
}

/**
 * Full-page overlay showing a teammate's planning (read-only week/month/schedule).
 */
const TeamMemberPlanningModal = ({ isOpen, member, onClose }) => {
  const { t } = useTranslation(['planning', 'common']);
  const weeklyViewRef = useRef(null);
  const monthViewRef = useRef(null);
  const scheduleViewRef = useRef(null);
  const [calendarMode, setCalendarMode] = useState('week');
  const [weekRangeLabel, setWeekRangeLabel] = useState('');
  const [monthRangeLabel, setMonthRangeLabel] = useState('');
  const [scheduleRangeLabel, setScheduleRangeLabel] = useState('');

  const handleWeekRangeChange = useCallback((label) => {
    setWeekRangeLabel(label || '');
  }, []);
  const handleMonthRangeChange = useCallback((label) => {
    setMonthRangeLabel(label || '');
  }, []);
  const handleScheduleRangeChange = useCallback((label) => {
    setScheduleRangeLabel(label || '');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setCalendarMode('week');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const displayName = getUserDisplayName(member, t('teamMember'));
  const isWeek = calendarMode === 'week';
  const isMonth = calendarMode === 'month';
  const isSchedule = calendarMode === 'schedule';

  const rangeLabel = isSchedule
    ? scheduleRangeLabel
    : isMonth
      ? monthRangeLabel
      : weekRangeLabel;

  const goPrev = () => {
    if (isWeek) weeklyViewRef.current?.goPrevDays?.();
    else if (isMonth) monthViewRef.current?.goPrevMonth?.();
    else scheduleViewRef.current?.goPrevMonth?.();
  };

  const goNext = () => {
    if (isWeek) weeklyViewRef.current?.goNextDays?.();
    else if (isMonth) monthViewRef.current?.goNextMonth?.();
    else scheduleViewRef.current?.goNextMonth?.();
  };

  const pillActive =
    'px-3 py-1 text-xs font-bold bg-white dark:bg-slate-700 shadow-sm rounded-md text-blue-600 dark:text-blue-400';
  const pillIdle =
    'px-3 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors rounded-md';

  return (
    <AnimatePresence>
      {isOpen && member ? (
        <div
          key="team-member-planning-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="team-member-planning-title"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-slate-200 dark:border-slate-600"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-600 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <h2
                  id="team-member-planning-title"
                  className="text-lg sm:text-xl font-bold text-slate-900 dark:text-slate-100 truncate"
                >
                  {t('membersPlanning', { name: displayName })}
                </h2>
                <div className="mt-1 flex items-center gap-1">
                  <button
                    type="button"
                    className="p-1.5 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    aria-label={isWeek ? t('previousWeek') : t('previousMonth')}
                    onClick={goPrev}
                  >
                    <span className="material-symbols-outlined text-xl">chevron_left</span>
                  </button>
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-300 min-w-[9rem] text-center">
                    {rangeLabel || '…'}
                  </span>
                  <button
                    type="button"
                    className="p-1.5 text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    aria-label={isWeek ? t('nextWeek') : t('nextMonth')}
                    onClick={goNext}
                  >
                    <span className="material-symbols-outlined text-xl">chevron_right</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex gap-1 bg-slate-100 dark:bg-slate-700/60 p-1 rounded-lg border border-slate-200 dark:border-slate-600">
                  <button
                    type="button"
                    className={isWeek ? pillActive : pillIdle}
                    onClick={() => setCalendarMode('week')}
                  >
                    {t('week')}
                  </button>
                  <button
                    type="button"
                    className={isMonth ? pillActive : pillIdle}
                    onClick={() => setCalendarMode('month')}
                  >
                    {t('month')}
                  </button>
                  <button
                    type="button"
                    className={isSchedule ? pillActive : pillIdle}
                    onClick={() => setCalendarMode('schedule')}
                  >
                    {t('schedule')}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  aria-label={t('common:close')}
                >
                  <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">close</span>
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 relative overflow-hidden">
              <AnimatePresence mode="wait">
                {isWeek ? (
                  <motion.div
                    key="weekly-view"
                    initial={{ opacity: 0, y: 20, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -16, scale: 0.985 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                    className="h-full min-h-0 w-full"
                  >
                    <EmployeeWeeklyViewPage
                      ref={weeklyViewRef}
                      userId={member.id}
                      readOnly
                      onWeekRangeChange={handleWeekRangeChange}
                    />
                  </motion.div>
                ) : isMonth ? (
                  <motion.div
                    key="month-view"
                    initial={{ opacity: 0, y: 20, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -16, scale: 0.985 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                    className="h-full min-h-0 w-full"
                  >
                    <EmployeeMonthViewPage
                      ref={monthViewRef}
                      userId={member.id}
                      readOnly
                      onMonthRangeChange={handleMonthRangeChange}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="schedule-view"
                    initial={{ opacity: 0, y: 20, scale: 0.985 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -16, scale: 0.985 }}
                    transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
                    className="h-full min-h-0 w-full"
                  >
                    <EmployeeScheduleViewPage
                      ref={scheduleViewRef}
                      userId={member.id}
                      readOnly
                      onScheduleRangeChange={handleScheduleRangeChange}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
};

export default TeamMemberPlanningModal;
